import { useState, useEffect } from "react";
import { Sliders, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const CURRENCIES = ["$", "£", "€", "¥", "C$", "A$"];

export default function SettingsDialog({ trigger }) {
  const { user, updateUserSettings } = useAuth();
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(45);
  const [multiplier, setMultiplier] = useState(1.5);
  const [currency, setCurrency] = useState("£");
  const [projects, setProjects] = useState([]);
  const [newProject, setNewProject] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user?.settings) {
      setRate(user.settings.hourly_rate);
      setMultiplier(user.settings.multiplier);
      setCurrency(user.settings.currency);
      setProjects(user.settings.projects || []);
    }
  }, [open, user]);

  const addProject = () => {
    const p = newProject.trim();
    if (p && !projects.includes(p)) {
      setProjects([...projects, p]);
      setNewProject("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hourly_rate: Number(rate),
        multiplier: Number(multiplier),
        currency,
        projects: projects.length ? projects : ["General"],
      };
      const { data } = await api.put("/settings", payload);
      updateUserSettings(data);
      toast.success("Settings saved");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-[#14161c] border border-[#2b2f3d] rounded-lg px-3 py-2.5 text-[#f3f4f8] outline-none focus:border-amber-500 transition-colors font-mono-data text-sm";
  const labelCls = "font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#181a22] border-[#2b2f3d] text-[#f3f4f8] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
            <Sliders className="w-5 h-5 text-amber-500" /> Settings
          </DialogTitle>
          <DialogDescription className="text-[#9fa6b9]">
            Set your default hourly rate, overtime multiplier, currency and project tags.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Default Rate / hr</label>
              <input data-testid="settings-hourly-rate-input" type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Default Multiplier</label>
              <input data-testid="settings-multiplier-input" type="number" step="0.1" min="0" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} className={`mt-1.5 ${inputCls}`} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Currency</label>
            <div className="mt-1.5 flex gap-2 flex-wrap">
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  data-testid={`settings-currency-${c}`}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-2 rounded-lg font-mono-data text-sm border transition-colors ${currency === c ? "bg-amber-500 text-[#0c0d10] border-amber-500" : "bg-[#14161c] text-[#9fa6b9] border-[#2b2f3d] hover:border-[#3a3f52]"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Projects / Categories</label>
            <div className="mt-1.5 flex gap-2">
              <input
                data-testid="settings-new-project-input"
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProject(); } }}
                className={inputCls}
                placeholder="Add a project tag"
              />
              <button data-testid="settings-add-project-button" onClick={addProject} className="shrink-0 px-3 bg-[#20232e] border border-[#2b2f3d] rounded-lg text-amber-500 hover:bg-[#2b2f3d] transition-colors">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {projects.map((p) => (
                <span key={p} className="flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono-data text-xs bg-[#20232e] text-[#f3f4f8] border border-[#2b2f3d]">
                  {p}
                  <button onClick={() => setProjects(projects.filter((x) => x !== p))} className="text-[#62697e] hover:text-red-400 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <button
            data-testid="settings-save-button"
            onClick={save}
            disabled={saving}
            className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0c0d10] font-display font-bold uppercase tracking-wide rounded-lg px-4 py-3 transition-colors active:scale-[0.99]"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
